import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import adminService from '../../services/api/admin';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getArabicDate } from '../../services/utils/date';
import { 
  BookOpen, 
  Plus, 
  Edit2, 
  Trash2, 
  Search, 
  Users, 
  Calendar, 
  ShieldCheck, 
  Layers, 
  CheckCircle2, 
  XCircle, 
  ChevronLeft, 
  X, 
  Save, 
  Info,
  DollarSign
} from 'lucide-react';

import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useToast } from '../../components/common/Toast';
import { useConfirm } from '../../hooks/useConfirm';
import { getImageUrl } from '../../config/apiConfig';
const ContentManagement = () => {
  const [activeTab, setActiveTab] = useState('courses');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialogComponent } = useConfirm();
  const toast = useToast();
  const navigate = useNavigate();
  // جلب المحتوى بناءً على التبويب النشط (المنطق الأصلي كاملاً)
  const { data: content, isLoading, error } = useQuery(
    ['admin-content', activeTab, searchTerm],
    () => adminService.getContent({ type: activeTab, search: searchTerm }),
    { keepPreviousData: true }
  );

  // Mutation لإنشاء محتوى جديد
  const createMutation = useMutation(
    async (data) => {
      const response = await adminService.createContent(activeTab, data);
      return response.data;
    },

    {
      onSuccess: () => {
        toast.success(`تم إنشاء ${activeTab === 'courses' ? 'الكورس' : 'الدرس'} بنجاح`);
        setShowCreateModal(false);
        queryClient.invalidateQueries(['admin-content']);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || `فشل إنشاء ${activeTab === 'courses' ? 'الكورس' : 'الدرس'}`);
      }
    }
  );

  // Mutation لتحديث المحتوى

  const updateMutation = useMutation(
    async ({ id, data }) => {
      const response = await adminService.updateContent(activeTab, id, data);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success(`تم تحديث ${activeTab === 'courses' ? 'الكورس' : 'الدرس'} بنجاح`);
        setEditingItem(null);
        setShowCreateModal(false);
        queryClient.invalidateQueries(['admin-content']);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || `فشل تحديث ${activeTab === 'courses' ? 'الكورس' : 'الدرس'}`);
      }
    }
  );

  // Mutation لحذف المحتوى

  const deleteMutation = useMutation(
    async (id) => {
      const response = await adminService.deleteContent(activeTab, id);
      return response.data;
    },

    {
      onSuccess: () => {
        toast.success(`تم حذف ${activeTab === 'courses' ? 'الكورس' : 'الدرس'} بنجاح`);
        queryClient.invalidateQueries(['admin-content']);
      },

      onError: (error) => {
        toast.error(error.response?.data?.error || `فشل حذف ${activeTab === 'courses' ? 'الكورس' : 'الدرس'}`);

      }
    }
  );
  const handleCreate = () => {
    // التوجيه إلى صفحة المدرس مع وضع admin mode
    if (activeTab === 'courses') {
      navigate('/teacher/create-course?mode=admin');
    } else {
      navigate('/teacher/create-lesson?mode=admin');
    }
  };

  const handleEdit = (item) => {
    // التوجيه إلى صفحة التعديل مع وضع admin mode
    if (activeTab === 'courses') {
      navigate(`/teacher/courses/${item.id}/edit?mode=admin`);
    } else {
      navigate(`/teacher/lessons/${item.id}/edit?mode=admin`);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await confirm({
      title: 'تأكيد الحذف الإداري',
      message: `هل أنت متأكد من حذف هذا ${activeTab === 'courses' ? 'الكورس' : 'الدرس'}؟ سيؤدي هذا لإزالته تماماً من المنصة والطلاب.`,
      type: 'danger'

    });

    if (confirmed) {
      deleteMutation.mutate(id);
    }
  };

  const handleSubmit = (data) => {
    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data });
    } else {
      createMutation.mutate(data);
    }
  };
  if (isLoading && !content) {
    return (

      <div className="flex flex-col items-center justify-center py-24 min-h-[500px]">

        <LoadingSpinner size="large" />

        <p className="mt-4 text-gray-500 font-bold animate-pulse">جاري تحميل لوحة المحتوى...</p>
      </div>
    );
  }

  return (

    <div className="max-w-7xl mx-auto space-y-10 px-4 sm:px-6 py-6 min-h-screen bg-gray-50/30">

      {/* Header Section */}

      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 lg:p-10 relative overflow-hidden">

        <div className="absolute top-0 left-0 w-32 h-32 bg-primary-50 rounded-br-[5rem] opacity-40"></div>

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">

          <div className="space-y-1">

            <h1 className="text-3xl lg:text-4xl font-black text-gray-900 tracking-tight flex items-center gap-3">

                <ShieldCheck className="text-primary-600" size={32} />

                إدارة المحتوى التعليمي

            </h1>

            <p className="text-gray-500 font-medium flex items-center gap-2">

                <Layers size={18} className="text-emerald-500" />

                تحكم كامل في كافة الكورسات والدروس المرفوعة من قبل المدرسين

            </p>

          </div>



          <div className="flex flex-wrap items-center gap-3">

            <button

              onClick={handleCreate}

              className="bg-primary-600 text-white px-8 py-3.5 rounded-2xl font-black text-sm hover:bg-primary-700 transition-all shadow-xl shadow-primary-100 flex items-center gap-2 transform active:scale-95"

            >

              <Plus size={20} /> إنشاء {activeTab === 'courses' ? 'كورس' : 'درس'} جديد

            </button>

          </div>

        </div>

      </div>



      {/* Tabs & Controls Bar */}

      <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-4">

        <div className="flex flex-col lg:flex-row items-center justify-between gap-6">

          {/* Professional Tab Switcher */}

          <div className="flex items-center gap-2 bg-gray-50 p-1.5 rounded-2xl border border-gray-100 w-full lg:w-fit">

            <button

              onClick={() => setActiveTab('courses')}

              className={`flex-1 lg:flex-none flex items-center justify-center gap-2 px-8 py-2.5 rounded-xl font-black text-sm transition-all duration-300 ${

                activeTab === 'courses' ? 'bg-white text-primary-600 shadow-md' : 'text-gray-400 hover:text-gray-600'

              }`}

            >

              <BookOpen size={18} />

              الكورسات ({activeTab === 'courses' ? content?.data?.length || 0 : '-'})

            </button>

            <button

              onClick={() => setActiveTab('lessons')}

              className={`flex-1 lg:flex-none flex items-center justify-center gap-2 px-8 py-2.5 rounded-xl font-black text-sm transition-all duration-300 ${

                activeTab === 'lessons' ? 'bg-white text-primary-600 shadow-md' : 'text-gray-400 hover:text-gray-600'

              }`}

            >

              <Layers size={18} />

              الدروس ({activeTab === 'lessons' ? content?.data?.length || 0 : '-'})

            </button>

          </div>



          {/* Search Box */}

          <div className="relative group w-full lg:max-w-md">

            <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary-500 transition-colors w-5 h-5" />

            <input

              type="text"

              placeholder={`ابحث عن ${activeTab === 'courses' ? 'كورس' : 'درس'} بالاسم...`}

              value={searchTerm}

              onChange={(e) => setSearchTerm(e.target.value)}

              className="w-full pr-12 pl-4 py-3.5 bg-gray-50 border border-transparent rounded-2xl focus:bg-white focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-medium"

            />

          </div>

        </div>

      </div>



      {/* Content List Table */}

      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden group transition-all duration-500 hover:shadow-xl">

        {error ? (

          <div className="py-20 text-center space-y-4">

            <XCircle className="w-16 h-16 text-red-500 mx-auto" />

            <p className="text-red-600 font-bold text-lg">حدث خطأ أثناء محاولة تحميل البيانات</p>

            <button onClick={() => window.location.reload()} className="bg-gray-100 px-6 py-2 rounded-xl text-sm font-bold">إعادة المحاولة</button>

          </div>

        ) : (activeTab === 'courses' ? content?.data?.length === 0 : content?.data?.length === 0) ? (

            <div className="py-24 text-center">

                <div className="w-32 h-32 mx-auto mb-6">

                    <div className="w-full h-full bg-gradient-to-br from-primary-50 to-primary-100 rounded-[2.5rem] flex items-center justify-center shadow-sm">

                        <div className="text-center">

                            <div className="w-16 h-16 mx-auto mb-2 bg-primary-200 rounded-2xl flex items-center justify-center">

                                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">

                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />

                                </svg>

                            </div>

                            <p className="text-xs text-primary-600 font-medium">كورس تعليمي</p>

                        </div>

                    </div>

                </div>

                <h3 className="text-2xl font-black text-gray-900 mb-2">لا يوجد محتوى مطابق</h3>

                <p className="text-gray-500 font-medium max-w-sm mx-auto leading-relaxed">لم يتم العثور على أي بيانات في قسم "{activeTab === 'courses' ? 'الكورسات' : 'الدروس'}" تطابق بحثك الحالي.</p>

            </div>

        ) : (

          <div className="overflow-x-auto">

            <table className="min-w-full divide-y divide-gray-50">

              <thead className="bg-gray-50/50">

                <tr>

                  <th className="px-8 py-6 text-right text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">بيانات العنوان</th>

                  <th className="px-8 py-6 text-right text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">المدرس المسؤول</th>

                  <th className="px-8 py-6 text-right text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">التكلفة</th>

                  <th className="px-8 py-6 text-right text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">الحالة</th>

                  <th className="px-8 py-6 text-right text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">تاريخ الإنشاء</th>

                  <th className="px-8 py-6 text-left text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">الإجراءات</th>

                </tr>

              </thead>

              <tbody className="bg-white divide-y divide-gray-50">

                {(activeTab === 'courses' ? content?.data : content?.data)?.map((item) => (

                  <tr key={item.id} className="hover:bg-primary-50/30 transition-colors group/row">

                    <td className="px-8 py-6 whitespace-nowrap">

                      <div className="flex flex-col">

                        <div className="text-base font-black text-gray-900 group-hover/row:text-primary-600 transition-colors truncate max-w-xs">{item.title}</div>

                        {item.description && (

                            <div className="text-xs font-bold text-gray-400 truncate max-w-xs mt-1">{item.description}</div>

                        )}

                      </div>

                    </td>

                    <td className="px-8 py-6 whitespace-nowrap">

                      <div className="flex items-center gap-3">

                        <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center text-white font-black shadow-md overflow-hidden">


                          {item.teacher_avatar ? (

                            <img src={getImageUrl(item.teacher_avatar)} alt="" className="w-full h-full object-cover" />

                          ) : (

                            <Users size={20} />

                          )}

                        </div>

                        <div className="text-sm font-black text-gray-700">{item.teacher_name}</div>

                      </div>

                    </td>

                    <td className="px-8 py-6 whitespace-nowrap">

                      <div className="flex items-center gap-1.5 text-sm font-black text-gray-900 bg-gray-50 px-3 py-1.5 rounded-xl border border-gray-100 w-fit">

                        {item.price ? (

                            <><span>{item.price}</span><span className="text-[10px] text-gray-400">ج.م</span></>

                        ) : (

                            <span className="text-green-600">مجاني</span>

                        )}

                      </div>

                    </td>

                    <td className="px-8 py-6 whitespace-nowrap">

                      <span className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest border ${

                        item.is_active

                          ? 'bg-emerald-50 text-emerald-700 border-emerald-100'

                          : 'bg-rose-50 text-rose-700 border-rose-100'

                      }`}>

                        {item.is_active ? <CheckCircle2 size={12} /> : <XCircle size={12} />}

                        {item.is_active ? 'نشط' : 'غير نشط'}

                      </span>

                    </td>

                    <td className="px-8 py-6 whitespace-nowrap">

                        <div className="flex items-center gap-2 text-xs font-bold text-gray-500">

                            <Calendar size={14} className="text-primary-500" />

                            {getArabicDate(item.created_at)}

                        </div>

                    </td>

                    <td className="px-8 py-6 whitespace-nowrap text-left">

                      <div className="flex items-center justify-end gap-2 opacity-0 group-hover/row:opacity-100 transition-opacity">

                        <button

                          onClick={() => handleEdit(item)}

                          className="w-10 h-10 flex items-center justify-center bg-white border border-gray-100 text-indigo-600 rounded-xl hover:bg-indigo-600 hover:text-white transition-all shadow-sm"

                          title="تعديل"

                        >

                          <Edit2 size={16} />

                        </button>

                        <button

                          onClick={() => handleDelete(item.id)}

                          className="w-10 h-10 flex items-center justify-center bg-white border border-gray-100 text-red-600 rounded-xl hover:bg-red-600 hover:text-white transition-all shadow-sm"

                          title="حذف"

                        >

                          <Trash2 size={16} />

                        </button>

                      </div>

                    </td>

                  </tr>

                ))}

              </tbody>

            </table>

          </div>

        )}

      </div>



      {/* Create/Edit Modern Modal */}

      {showCreateModal && (

        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[300] p-4 animate-in fade-in duration-300">

          <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-2xl w-full relative overflow-hidden">

            

            {/* Modal Header */}

            <div className="flex items-center justify-between p-8 border-b border-gray-100 bg-gray-50/50">

              <div className="flex items-center gap-3">

                <div className="w-12 h-12 bg-primary-600 rounded-2xl flex items-center justify-center text-white shadow-xl shadow-primary-100">

                    {editingItem ? <Edit2 size={24} /> : <Plus size={24} />}

                </div>

                <div>

                    <h3 className="text-xl font-black text-gray-900 leading-none">

                        {editingItem ? `تعديل بيانات ${activeTab === 'courses' ? 'الكورس' : 'الدرس'}` : `إنشاء ${activeTab === 'courses' ? 'كورس' : 'درس'} جديد`}

                    </h3>

                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-1.5">أدخل البيانات المطلوبة بدقة</p>

                </div>

              </div>

              <button

                onClick={() => setShowCreateModal(false)}

                className="w-10 h-10 flex items-center justify-center bg-white text-gray-400 hover:bg-red-50 hover:text-red-500 rounded-xl transition-all border border-gray-100"

              >

                <X size={20} />

              </button>

            </div>

            

            {/* Modal Body */}

            <form onSubmit={(e) => {

              e.preventDefault();

              const formData = new FormData(e.target);

              const data = Object.fromEntries(formData.entries());

              // تحويل الحالة من string إلى boolean

              data.is_active = data.is_active === 'true';

              handleSubmit(data);

            }} className="p-8 lg:p-10 space-y-8">

              

              <div className="space-y-2">

                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">العنوان الرئيسي للمحتوى *</label>

                <input

                  type="text"

                  name="title"

                  defaultValue={editingItem?.title || ''}

                  required

                  placeholder="مثال: مقدمة في الرياضيات المتقدمة"

                  className="w-full px-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-900"

                />

              </div>

              

              <div className="space-y-2">

                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">وصف مختصر للمحتوى</label>

                <textarea

                  name="description"

                  rows={4}

                  defaultValue={editingItem?.description || ''}

                  placeholder="اكتب نبذة توضح محتوى الكورس أو الدرس..."

                  className="w-full px-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-medium text-gray-700 resize-none leading-relaxed"

                />

              </div>

              

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                <div className="space-y-2">

                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">التكلفة (ج.م) *</label>

                  <div className="relative">

                    <input

                        type="number"

                        name="price"

                        defaultValue={editingItem?.price || ''}

                        min="0"

                        step="0.01"

                        placeholder="اتركه فارغاً إذا كان مجانياً"

                        className="w-full px-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-900"

                    />

                    <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />

                  </div>

                </div>

                

                <div className="space-y-2">

                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">حالة الظهور</label>

                  <div className="relative">

                    <select

                        name="is_active"

                        defaultValue={editingItem?.is_active !== undefined ? editingItem.is_active.toString() : 'true'}

                        className="w-full px-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-700 appearance-none cursor-pointer"

                    >

                        <option value="true">نشط (ظاهر للجميع)</option>

                        <option value="false">غير نشط (مخفي)</option>

                    </select>

                    <ChevronLeft className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 -rotate-90" size={18} />

                  </div>

                </div>

              </div>

              

              <div className="pt-6 grid grid-cols-2 gap-4">

                <button

                  type="button"

                  onClick={() => setShowCreateModal(false)}

                  className="px-6 py-4 border border-gray-100 rounded-2xl text-gray-400 font-black text-sm hover:bg-gray-50 transition-all uppercase tracking-widest"

                >

                  إلغاء العملية

                </button>

                <button

                  type="submit"

                  disabled={createMutation.isLoading || updateMutation.isLoading}

                  className="px-6 py-4 bg-primary-600 text-white rounded-2xl font-black text-sm shadow-xl shadow-primary-100 hover:bg-primary-700 transition-all transform active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"

                >

                  {(createMutation.isLoading || updateMutation.isLoading) ? <LoadingSpinner size="small" /> : <Save size={18} />}

                  <span>{editingItem ? 'حفظ التعديلات' : 'إتمام الإضافة'}</span>

                </button>

              </div>

            </form>

          </div>

        </div>

      )}



      {/* Floating Info Section */}

      <div className="bg-gray-900 rounded-[2.5rem] p-10 text-white flex flex-col md:flex-row items-center justify-between gap-8 relative overflow-hidden group shadow-2xl">

        <div className="absolute top-0 right-0 w-64 h-64 bg-primary-600/20 rounded-full blur-3xl -z-0"></div>

        <div className="relative z-10 text-center md:text-right space-y-2">

            <h3 className="text-2xl font-black flex items-center gap-2 justify-center md:justify-start leading-tight">

                <Info className="text-primary-500" /> ملاحظة إدارية هامة

            </h3>

            <p className="text-gray-400 font-medium max-w-xl leading-relaxed">أي تعديل يتم على سعر الكورس أو حالته سيظهر فوراً لجميع الطلاب والاشتراكات الحالية. تأكد من مراجعة البيانات قبل النشر.</p>

        </div>

        <button className="relative z-10 bg-white text-gray-900 px-10 py-4 rounded-2xl font-black flex items-center gap-2 shadow-xl hover:bg-primary-50 transition-all transform active:scale-95">

            تحميل تقارير المحتوى <ChevronLeft size={20} />

        </button>

      </div>



      <ConfirmDialogComponent />

    </div>

  );

};



export default ContentManagement;